import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

import { CommonTableColumnsEnum } from '../../components/Table/types'
import { PagePagination, SortDirectionCapital } from '../../types'
import { getInitialPaginationData, getInitialTableColumns } from '../../utils'

import { ImportHistory, LogHistory, SettingsState } from './types'

export const fetchLogHistoryData = createAsyncThunk(
  'settings/fetchLogHistory',
  async (
    pagination: Pick<PagePagination<LogHistory>, 'page' | 'perPage' | 'order' | 'orderBy'>,
  ) => {
    try {
      const { SettingsService } = await import('./SettingsService')
      const { data: response } = await SettingsService.getLogHistory(pagination)
      const { data, meta } = response
      return { data, meta }
    } catch (error) {
      throw new Error(error.message)
    }
  },
)

export const fetchImportHistoryData = createAsyncThunk(
  'settings/fetchImportHistory',
  async (
    pagination: Pick<PagePagination<ImportHistory>, 'page' | 'perPage' | 'order' | 'orderBy'>,
  ) => {
    try {
      const { SettingsService } = await import('./SettingsService')
      const { data: response } = await SettingsService.getImportHistory(pagination)
      const { data, meta } = response
      return { data, meta }
    } catch (error) {
      throw new Error(error.message)
    }
  },
)

const initialLogHistoryPosition = {
  data: [],
  pagination: {
    ...getInitialPaginationData<LogHistory>(
      getInitialTableColumns().logHistory as (keyof LogHistory)[],
    ),
    order: SortDirectionCapital.desc,
    orderBy: CommonTableColumnsEnum.date as keyof LogHistory,
  },
}

const initialImportHistoryPosition = {
  data: [],
  pagination: {
    ...getInitialPaginationData<ImportHistory>(
      getInitialTableColumns().importHistory as (keyof ImportHistory)[],
    ),
    order: SortDirectionCapital.desc,
    orderBy: CommonTableColumnsEnum.date as keyof ImportHistory,
  },
}

const initialState: SettingsState = {
  logHistory: initialLogHistoryPosition,
  importHistory: initialImportHistoryPosition,
}

const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    updateLogHistoryOrder: (state, action) => {
      state.logHistory.pagination.columns = action.payload
    },
    updateLogHistorySortOrder: (state, action) => {
      state.logHistory.pagination.page = 1
      state.logHistory.pagination.order = action.payload
    },
    updateLogHistoryOrderBy: (state, action) => {
      state.logHistory.pagination.page = 1
      state.logHistory.pagination.order =
        action.payload === CommonTableColumnsEnum.date
          ? SortDirectionCapital.desc
          : SortDirectionCapital.asc
      state.logHistory.pagination.orderBy = action.payload
    },
    updateLogHistoryPageSize: (state, action) => {
      state.logHistory.pagination.perPage = action.payload
    },
    updateLogHistoryPage: (state, action) => {
      state.logHistory.pagination.page = action.payload
    },
    updateImportHistoryOrder: (state, action) => {
      state.importHistory.pagination.columns = action.payload
    },
    updateImportHistoryPageSize: (state, action) => {
      state.importHistory.pagination.perPage = action.payload
    },
    updateImportHistoryPage: (state, action) => {
      state.importHistory.pagination.page = action.payload
    },
    updateImportHistorySortOrder: (state, action) => {
      state.importHistory.pagination.page = 1
      state.importHistory.pagination.order = action.payload
    },
    updateImportHistoryOrderBy: (state, action) => {
      state.importHistory.pagination.page = 1
      state.importHistory.pagination.order =
        action.payload === CommonTableColumnsEnum.date
          ? SortDirectionCapital.desc
          : SortDirectionCapital.asc
      state.importHistory.pagination.orderBy = action.payload
    },
    clearAllSettings: (state) => {
      state.importHistory = initialState.importHistory
      state.logHistory = initialState.logHistory
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLogHistoryData.fulfilled, (state, action) => {
        const { data, meta } = action.payload
        state.logHistory = {
          data: data,
          pagination: { ...state.logHistory.pagination, ...meta },
        }
      })
      .addCase(fetchImportHistoryData.fulfilled, (state, action) => {
        const { data, meta } = action.payload
        state.importHistory = {
          data: data,
          pagination: { ...state.importHistory.pagination, ...meta },
        }
      })
  },
})

const SettingsSliceReducer = settingsSlice.reducer

export const {
  updateLogHistoryOrder,
  updateLogHistoryOrderBy,
  updateLogHistorySortOrder,
  updateLogHistoryPage,
  updateLogHistoryPageSize,
  updateImportHistoryOrder,
  updateImportHistoryPage,
  updateImportHistoryPageSize,
  updateImportHistorySortOrder,
  updateImportHistoryOrderBy,
  clearAllSettings,
} = settingsSlice.actions

export default SettingsSliceReducer
